import React, { useCallback, useEffect, useState } from "react";
import {
  HeartPulse,
  Clock,
  Coins,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Stethoscope,
} from "lucide-react";
import { callBackend } from "../utils/api";

export default function Hospital({ user, fetchStats }) {
  const [patients, setPatients] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callBackend(`/hospital/patients?page=${page}`);
      setPatients(res.patients);
      setTotalPages(res.totalPages);
    } catch (err) {
      console.error("Intelligence failure: Hospital records unreachable.");
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const handleRecover = async () => {
    try {
      await callBackend("/hospital/recover", { method: "POST" });
      fetchStats();
      fetchPatients();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b-2 border-zinc-900 pb-6">
        <div>
          <h2 className="text-4xl font-black italic uppercase text-red-600 flex items-center gap-3">
            <HeartPulse size={36} /> Central General
          </h2>
          <p className="text-zinc-500 text-xs font-bold uppercase tracking-[0.2em]">
            The triage for the broken and the dead.
          </p>
        </div>

        {user.state !== "alive" && (
          <button
            onClick={handleRecover}
            className="bg-green-600 text-black px-6 py-3 font-black uppercase italic skew-x-[-12deg] hover:bg-white transition-all shadow-[4px_4px_0px_0px_rgba(34,197,94,0.3)] flex items-center gap-2"
          >
            <Coins size={18} /> Bribe Staff ($50,000)
          </button>
        )}
      </div>

      {/* Patients Table */}
      <div className="bg-zinc-950 border-2 border-zinc-900 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] overflow-hidden">
        <div className="bg-zinc-900/50 p-4 border-b border-zinc-800 flex items-center gap-2">
          <Stethoscope size={16} className="text-zinc-500" />
          <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400">
            Current Admissions
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-[11px] font-bold uppercase">
            <thead>
              <tr className="border-b border-zinc-900 text-zinc-500 bg-black">
                <th className="p-4">Patient Alias</th>
                <th className="p-4">Condition</th>
                <th className="p-4">Recovery Time</th>
                <th className="p-4">Notes</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="4"
                    className="p-12 text-center text-zinc-700 italic font-mono"
                  >
                    Scanning medical database...
                  </td>
                </tr>
              ) : patients.length === 0 ? (
                <tr>
                  <td
                    colSpan="4"
                    className="p-12 text-center text-zinc-700 italic font-mono"
                  >
                    No active patients. The streets are unusually quiet.
                  </td>
                </tr>
              ) : (
                patients.map((p) => (
                  <tr
                    key={p.id}
                    className="border-b border-zinc-900/50 hover:bg-zinc-900/20 transition-colors"
                  >
                    <td className="p-4 text-white italic">{p.username}</td>
                    <td className="p-4">
                      <span
                        className={`px-2 py-0.5 border ${p.state === "deceased" ? "border-red-900 text-red-500" : "border-yellow-900 text-yellow-500"}`}
                      >
                        {p.state}
                      </span>
                    </td>
                    <td className="p-4 text-zinc-400 flex items-center gap-2">
                      <Clock size={12} /> {p.minutes_left}m
                    </td>
                    <td className="p-4 text-zinc-600 font-medium normal-case italic">
                      {p.hospitalization_reason || "Unknown trauma"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Paging Footer */}
        <div className="bg-black p-4 border-t border-zinc-900 flex justify-between items-center">
          <div className="text-[9px] text-zinc-600 font-black uppercase">
            Surgeon Capacity: {page} / {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="p-2 border border-zinc-800 text-zinc-500 hover:text-white disabled:opacity-30"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="p-2 border border-zinc-800 text-zinc-500 hover:text-white disabled:opacity-30"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Tactical Note */}
      <div className="border-l-4 border-red-600 bg-red-950/10 p-4 flex items-start gap-3">
        <AlertTriangle className="text-red-600 shrink-0" size={20} />
        <p className="text-[10px] text-red-500/80 uppercase font-bold leading-relaxed">
          Tactical Advisory: Patients in a 'Deceased' state have zero street
          power. Use this window to seize their territory or pressure their
          alliance while they are flatlined.
        </p>
      </div>
    </div>
  );
}
